import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Mail, ChevronLeft, DollarSign, Users, Sparkles, Zap } from 'lucide-react';

const AffiliatesPage = () => {
  const [form, setForm] = useState({ name: '', email: '', audience: '', reach: '', message: '' });
  const [status, setStatus] = useState(null); // null | 'submitting' | 'success' | 'error'

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('submitting');
    try {
      const res = await fetch('https://app.getsortio.com/api/contact-or-suggest/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'affiliate',
          name: form.name,
          email: form.email,
          // Structured affiliate context packed into the message so it is all
          // captured without a schema change (stored as message_type='affiliate').
          message:
            `Platform / channels: ${form.audience || '(not provided)'}\n` +
            `Audience size / reach: ${form.reach || '(not provided)'}\n\n` +
            form.message,
        }),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', audience: '', reach: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (_) {
      setStatus('error');
    }
  };

  const perks = [
    {
      title: "Generous commissions",
      description: "30% recurring, for life. On a Pro subscription that is $29.70 a year per referral, for as long as they stay subscribed. That is our floor, and we go higher for partners with strong audience fit.",
      icon: <DollarSign className="w-6 h-6" />,
      color: "text-sortio-primary",
    },
    {
      title: "Recurring revenue",
      description: "Sortio is a subscription product. Your commissions continue as long as your referrals stay subscribed.",
      icon: <Zap className="w-6 h-6" />,
      color: "text-sortio-secondary",
    },
    {
      title: "A product people love",
      description: "Sortio solves a real problem — file organization — with AI that actually works. Easy to recommend, easy to demo.",
      icon: <Sparkles className="w-6 h-6" />,
      color: "text-sortio-accent",
    },
    {
      title: "Real partnership",
      description: "We work directly with our affiliates. Co-marketing, custom assets, early access to features — whatever helps you succeed.",
      icon: <Users className="w-6 h-6" />,
      color: "text-blue-400",
    },
  ];

  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <Helmet>
        <title>Become a Sortio Affiliate</title>
        <meta
          name="description"
          content="Join the Sortio affiliate program. Earn generous commissions promoting the AI file organizer. Contact marcus@getsortio.com to get started."
        />
      </Helmet>

      {/* Background gradient */}
      <div className="absolute top-0 inset-x-0 h-[500px] bg-gradient-to-b from-apple-700/10 to-transparent"></div>
      <div className="absolute -top-[600px] -right-[300px] w-[1000px] h-[1000px] bg-sortio-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-[600px] -left-[300px] w-[1000px] h-[1000px] bg-sortio-secondary/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        {/* Back button */}
        <Link
          to="/"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-12 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm">Back to home</span>
        </Link>

        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-3 py-1 mb-6 rounded-full bg-sortio-primary/10 border border-sortio-primary/20">
            <span className="text-xs font-medium uppercase tracking-wider text-sortio-primary">Affiliate Program</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-apple-50 to-sortio-primary">
            Become a Sortio Affiliate
          </h1>
          <p className="text-xl text-apple-300 max-w-2xl mx-auto leading-relaxed">
            Earn 30% recurring commissions, for life, on every paying subscriber you refer. That is our floor, with more for the right audience fit.
          </p>
        </div>

        {/* Affiliate application form — primary CTA */}
        <div id="apply" className="bg-gradient-to-br from-sortio-primary/10 to-sortio-secondary/10 border border-sortio-primary/30 rounded-2xl p-8 md:p-10 mb-16 backdrop-blur">
          <h2 className="text-2xl font-semibold text-apple-50 mb-2">Tell us about you</h2>
          <p className="text-apple-300 mb-6">
            Share a bit about who you are and your audience. If it is a fit, Marcus follows up personally, usually within a day or two.
          </p>

          {status === 'success' ? (
            <div className="rounded-xl bg-sortio-primary/10 border border-sortio-primary/30 p-6 text-center">
              <h3 className="text-lg font-semibold text-apple-50 mb-1">Thanks, we got it.</h3>
              <p className="text-apple-300">We will be in touch at the email you provided. Talk soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-apple-300 mb-2">Your name</label>
                  <input type="text" required value={form.name} onChange={update('name')}
                    className="w-full rounded-xl bg-apple-900/60 border border-apple-800/60 px-4 py-3 text-apple-100 placeholder-apple-500 focus:outline-none focus:border-sortio-primary/60 transition-colors"
                    placeholder="Jane Doe" />
                </div>
                <div>
                  <label className="block text-sm text-apple-300 mb-2">Email</label>
                  <input type="email" required value={form.email} onChange={update('email')}
                    className="w-full rounded-xl bg-apple-900/60 border border-apple-800/60 px-4 py-3 text-apple-100 placeholder-apple-500 focus:outline-none focus:border-sortio-primary/60 transition-colors"
                    placeholder="you@example.com" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-apple-300 mb-2">Where is your audience? (channels and links)</label>
                  <input type="text" value={form.audience} onChange={update('audience')}
                    className="w-full rounded-xl bg-apple-900/60 border border-apple-800/60 px-4 py-3 text-apple-100 placeholder-apple-500 focus:outline-none focus:border-sortio-primary/60 transition-colors"
                    placeholder="YouTube, newsletter, blog, community (links welcome)" />
                </div>
                <div>
                  <label className="block text-sm text-apple-300 mb-2">Audience size / reach</label>
                  <input type="text" value={form.reach} onChange={update('reach')}
                    className="w-full rounded-xl bg-apple-900/60 border border-apple-800/60 px-4 py-3 text-apple-100 placeholder-apple-500 focus:outline-none focus:border-sortio-primary/60 transition-colors"
                    placeholder="e.g. 20k subscribers, 5k newsletter" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-apple-300 mb-2">Who is your audience, and how would you promote Sortio?</label>
                <textarea required rows={5} value={form.message} onChange={update('message')}
                  className="w-full rounded-xl bg-apple-900/60 border border-apple-800/60 px-4 py-3 text-apple-100 placeholder-apple-500 focus:outline-none focus:border-sortio-primary/60 transition-colors resize-y"
                  placeholder="Tell us about your audience and how you would like to share Sortio." />
              </div>
              {status === 'error' && (
                <p className="text-sm text-red-400">Something went wrong sending that. Please try again, or email marcus@getsortio.com.</p>
              )}
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <button type="submit" disabled={status === 'submitting'}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-sortio-primary hover:bg-sortio-primary/90 disabled:opacity-60 text-white font-semibold transition-all duration-200 shadow-md hover:shadow-lg">
                  {status === 'submitting' ? 'Sending...' : 'Apply to the affiliate program'}
                </button>
                <span className="text-sm text-apple-400">or email <a className="text-sortio-primary hover:underline" href="mailto:marcus@getsortio.com?subject=Sortio%20Affiliate%20Program">marcus@getsortio.com</a></span>
              </div>
            </form>
          )}
        </div>

        {/* Why join */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold text-apple-50 mb-8 text-center">Why join the Sortio affiliate program</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {perks.map((perk, i) => (
              <div
                key={i}
                className="bg-apple-900/40 border border-apple-800/40 rounded-xl p-6 backdrop-blur hover:border-sortio-primary/40 transition-colors"
              >
                <div className={`w-12 h-12 rounded-xl bg-apple-800/60 flex items-center justify-center mb-4 ${perk.color}`}>
                  {perk.icon}
                </div>
                <h3 className="text-lg font-semibold text-apple-50 mb-2">{perk.title}</h3>
                <p className="text-apple-300 text-sm leading-relaxed">{perk.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Who we're looking for */}
        <div className="bg-apple-900/30 border border-apple-800/40 rounded-2xl p-8 md:p-10 mb-16 backdrop-blur">
          <h2 className="text-2xl font-semibold text-apple-50 mb-6">Who we're looking for</h2>
          <p className="text-apple-300 mb-6 leading-relaxed">
            We partner with creators, publishers, and communities whose audiences genuinely care about productivity, privacy, and working smarter. A few examples:
          </p>
          <ul className="space-y-3 text-apple-200">
            <li className="flex items-start">
              <span className="text-sortio-primary mr-3 mt-1">•</span>
              <span>Productivity and tech YouTubers</span>
            </li>
            <li className="flex items-start">
              <span className="text-sortio-primary mr-3 mt-1">•</span>
              <span>Newsletter writers covering software, AI, or workflows</span>
            </li>
            <li className="flex items-start">
              <span className="text-sortio-primary mr-3 mt-1">•</span>
              <span>Freelancer and small-business communities</span>
            </li>
            <li className="flex items-start">
              <span className="text-sortio-primary mr-3 mt-1">•</span>
              <span>Professionals in document-heavy fields (law, accounting, research)</span>
            </li>
            <li className="flex items-start">
              <span className="text-sortio-primary mr-3 mt-1">•</span>
              <span>Bloggers and reviewers in the Mac, Windows, and Linux ecosystems</span>
            </li>
          </ul>
          <p className="text-apple-300 mt-6 leading-relaxed">
            Not on this list? Reach out anyway — if your audience is the right fit, we want to hear from you.
          </p>
        </div>

        {/* How it works */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold text-apple-50 mb-8 text-center">How it works</h2>
          <div className="space-y-4">
            {[
              { n: 1, text: "Apply with the form above, telling us a bit about your audience." },
              { n: 2, text: "We confirm your commission rate (30% to start, higher for strong audience fit) and set you up with a unique referral link." },
              { n: 3, text: "You share Sortio with your audience however makes sense — videos, newsletters, reviews, posts." },
              { n: 4, text: "You earn commissions on every paying subscriber who signs up through your link." },
            ].map((step) => (
              <div key={step.n} className="flex items-start gap-4">
                <div className="shrink-0 w-8 h-8 rounded-full bg-sortio-primary/20 border border-sortio-primary/40 flex items-center justify-center text-sortio-primary font-semibold text-sm">
                  {step.n}
                </div>
                <p className="text-apple-200 pt-1">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Secondary CTA */}
        <div className="text-center bg-apple-900/40 border border-apple-800/40 rounded-2xl p-8 backdrop-blur">
          <h2 className="text-2xl font-semibold text-apple-50 mb-3">Let's talk</h2>
          <p className="text-apple-300 mb-6">Questions, ideas, or ready to sign up? Marcus reads every email.</p>
          <a
            href="mailto:marcus@getsortio.com?subject=Sortio%20Affiliate%20Program"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-sortio-primary hover:bg-sortio-primary/90 text-white font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <Mail className="w-5 h-5" />
            marcus@getsortio.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default AffiliatesPage;
